import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Server } from "http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { tap } from "rxjs/internal/operators/tap";
import { catchError, map, switchMap, take, timeout } from "rxjs/operators";
import { Currency } from "src/app/data/metal-list";
import { PriceResponse } from "src/app/model/price/price-response";
import { Product } from "src/app/model/price/product";
import { GoldCard } from "src/app/model/savings-plan/gold-card";
import { UserService } from "src/service/user.service";
import { ServerType } from "../../../service/app-init.service";
import { RestApiService } from "../../../service/rest-api.service";

export interface Price {
  name: Currency;
  buy: number;
  sell: number;
}

export interface ProductCategory {
  categoryTitle: string;
  categoryProducts: Product[];
}

@Injectable({
  providedIn: "root",
})
export class PriceService {
  constructor(
    private restApiService: RestApiService,
    private translateService: TranslateService,
    private userService:UserService
  ) {}



  endpoint = "price";
  priceList = new BehaviorSubject<Price[]>(null);
  priceJwt = new BehaviorSubject<string>(null);
  productList = new BehaviorSubject<Product[]>(null);
  publishedProductList = new BehaviorSubject<Product[]>([])
  goldCardList = new BehaviorSubject<GoldCard[]>(null);
  productCategories = new BehaviorSubject<ProductCategory[]>(null);
  highlightedProducts = new BehaviorSubject<Product[]>(null);
  rawProductPrices;

  fetchPriceList(): Observable<PriceResponse> {
    let params = new HttpParams();
    if(this.userService.user?.value?.country){
      params = params.append("country",this.userService.user?.value?.country );
    }

    return this.restApiService.get(ServerType.PRICE, this.endpoint, {params}).pipe(
      catchError((error) => {
        console.error(error);
        return this.fetchPriceList();
      }),
      timeout(2000),
      tap((response: PriceResponse) => this.parsePriceList(response))
    );
  }

  parsePriceList({ priceJwt, prices }: PriceResponse) {
    const currentList: Price[] = [];
    const metalPrices = prices.metalPrices;
    this.goldCardList.next(prices.goldCardPrices.filter(item=>item.publish));
    this.rawProductPrices = prices.productPrices;
    const languageCode = this.getProductLanguageCode(this.rawProductPrices);
    this.syncProductLanguage(languageCode);
    this.productCategories.next(
      this.getProductCategories(prices.productPrices)
    );

    this.getHighlightedProductsIds()
      .pipe(
        switchMap((ids: string[]) =>
          of(
            ids.reduce((acc, val) => {
              const product = this.productList.value.find(
                (item) => item.id == val
              );
              acc.push(product);
              return acc;
            }, [])
          )
        )
      )
      .subscribe((list) => this.highlightedProducts.next(list));

    currentList.push({
      name: Currency.GOLD,
      buy: metalPrices.au_buy,
      sell: metalPrices.au_sell,
    });
    currentList.push({
      name: Currency.SILVER,
      buy: metalPrices.ag_buy,
      sell: metalPrices.ag_sell,
    });
    currentList.push({
      name: Currency.PALLADIUM,
      buy: metalPrices.pd_buy,
      sell: metalPrices.pd_sell,
    });
    currentList.push({
      name: Currency.PLATIN,
      buy: metalPrices.pt_buy,
      sell: metalPrices.pt_sell,
    });
    this.priceList.next(currentList);
    this.priceJwt.next(priceJwt);
  }

  getHighlightedProductsIds() {
    return this.restApiService.get(
      ServerType.PRODUCT,
      "products/get-highlighted-products"
    );
  }

  getProductCategories(productList: Product[]): ProductCategory[] {
    return productList?.reduce((acc, val) => {
      const existingIndex = acc.findIndex(
        (item) => item.categoryTitle == val.productType
      );
      if (existingIndex != -1) {
        acc[existingIndex].categoryProducts.push(val);
      } else {
        const productCategory: ProductCategory = {
          categoryTitle: val.productType,
          categoryProducts: [val],
        };

        acc.push(productCategory);
      }
      return acc;
    }, []);
  }

  syncProductLanguage(languageCode: string) {
    const syncedProductList = this.rawProductPrices.map((product) => {
      let selectedLanguageObj = product.langNameDescObjList.find(
        (item) => item.language == languageCode
      );
      product.name = selectedLanguageObj?.name;
      product.description = selectedLanguageObj?.description;
      return product;
    });
    this.productList.next(syncedProductList);
    this.publishedProductList.next(syncedProductList.filter(item=>item.publish))
  }

  getProductLanguageCode(productList: Product[]) {
    let array = productList.reduce((acc, val) => {
      let languageCodeArray = val.langNameDescObjList.map(
        (item) => item.language
      );
      let distinctArray = languageCodeArray.filter(
        (code) => !acc.includes(code)
      );
      return [...acc, ...distinctArray];
    }, []);

    let browserLangugeCode = this.translateService.currentLang.toUpperCase();
    let index = array.indexOf(browserLangugeCode);
    if (index != -1) {
      return array[index];
    }

    if (array.includes("DE")) {
      return "DE";
    }
    return array[0];
  }

  getMetalSellPrice(currency: Currency) {
    return this.priceList.getValue().find((item) => item.name == currency).sell;
  }

  getTresorGoldPrices() {
    return this.priceList.pipe(
      map((list) => {
        const goldPrices = list.find((item) => item.name == Currency.GOLD);
        return { buyPrice: goldPrices.buy, sellPrice: goldPrices.sell };
      })
    );
  }

  syncProductPrice(product: Product) {
    return this.fetchPriceList().pipe(
      switchMap(() =>
        of(this.productList.value.find((mProduct) => mProduct.id == product.id))
      )
    );
  }

  getProductById(id: string) {
   return this.productList.pipe(
     take(1),
      map((productList) => productList?.find((product) => product.id == id)),
    );
  }

  getGoldCardById(id: string) {
    return this.goldCardList.pipe(
      map((goldCardList) => goldCardList?.find((goldCard) => goldCard.id == id))
    );
  }

  getPriceItem(name: Currency) {
    return this.priceList.value.find((item) => item.name == name);
  }
}
